import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, Loader2, Sword, Target, ChevronLeft, Calendar, ArrowDownUp, Flame, Snowflake, Shield } from "lucide-react";
import Navbar from "@/components/Navbar";
import { S, KatanaDivider, SamuraiBackground, SLOT_META } from "@/components/samurai/theme";
import useDailyUsage from "@/hooks/useDailyUsage";
import { usePremium } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function toISODate(d) {
  return d.toISOString().slice(0, 10);
}

function todayISO() {
  return toISODate(new Date());
}

function shiftDate(iso, days) {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + days);
  return toISODate(d);
}

function formatKickoff(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString("it-IT", {
      hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome",
    });
  } catch {
    return "—";
  }
}

function formatDateLabel(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T12:00:00").toLocaleDateString("it-IT", {
      weekday: "long", day: "2-digit", month: "long", timeZone: "Europe/Rome",
    });
  } catch {
    return iso;
  }
}

function DatePicker({ value, onChange }) {
  return (
    <div
      data-testid="samurai-date-picker"
      style={{
        display: "flex", alignItems: "stretch",
        background: S.panel, border: `1px solid ${S.hairline2}`,
        boxShadow: `0 0 0 1px ${S.bloodDim} inset`,
        marginBottom: "1rem",
      }}
    >
      <button
        data-testid="samurai-date-prev"
        onClick={() => onChange(shiftDate(value, -1))}
        style={{
          padding: "0 0.9rem", background: "transparent", border: "none",
          borderRight: `1px solid ${S.hairline}`, color: S.blood, cursor: "pointer",
          textShadow: `0 0 8px ${S.bloodGlow}`,
        }}
      >
        <ChevronLeft size={18} />
      </button>
      <div
        style={{
          flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
          gap: "0.6rem", padding: "0.7rem 0.8rem", minWidth: 0, position: "relative",
        }}
      >
        <Calendar size={14} style={{ color: S.blood, flexShrink: 0 }} />
        <span
          style={{
            fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
            fontSize: "0.95rem", letterSpacing: "0.15em", color: S.ink,
            textTransform: "uppercase",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}
        >
          {formatDateLabel(value)}
        </span>
        <input
          aria-label="Scegli data"
          data-testid="samurai-date-input"
          type="date"
          value={value}
          onChange={(e) => e.target.value && onChange(e.target.value)}
          style={{
            position: "absolute", inset: 0, opacity: 0, cursor: "pointer",
            border: "none", background: "transparent",
          }}
        />
      </div>
      <button
        data-testid="samurai-date-today"
        onClick={() => onChange(todayISO())}
        disabled={value === todayISO()}
        style={{
          padding: "0 0.9rem", background: value === todayISO() ? "transparent" : S.bloodDim,
          border: "none", borderLeft: `1px solid ${S.hairline}`,
          color: value === todayISO() ? S.muted : S.blood, cursor: "pointer",
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
          fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase",
          textShadow: value === todayISO() ? "none" : `0 0 8px ${S.bloodGlow}`,
        }}
      >
        Oggi
      </button>
      <button
        data-testid="samurai-date-next"
        onClick={() => onChange(shiftDate(value, 1))}
        style={{
          padding: "0 0.9rem", background: "transparent", border: "none",
          borderLeft: `1px solid ${S.hairline}`, color: S.blood, cursor: "pointer",
          textShadow: `0 0 8px ${S.bloodGlow}`,
        }}
      >
        <ChevronRight size={18} />
      </button>
    </div>
  );
}

function SlotLeaderboardCard({ slotKey, stats }) {
  const meta = SLOT_META[slotKey];
  const Icon = meta.icon;
  const hit = stats?.hit_rate;
  const sample = stats?.sample || 0;
  const streak = stats?.streak || 0;
  const last = stats?.last_outcomes || [];
  const hitColor =
    hit == null ? S.sub :
    hit >= 70 ? "#22FFA3" :
    hit >= 55 ? meta.color :
    hit >= 40 ? "#FFB020" :
    "#FF4040";
  const streakHot = streak >= 3;
  const streakIce = streak <= -3;
  return (
    <div
      data-testid={`leaderboard-${slotKey}`}
      style={{
        background: S.panel, border: `1px solid ${meta.color}40`,
        padding: "0.85rem 0.9rem",
        boxShadow: `0 0 16px -4px ${meta.glow}, inset 0 0 8px ${meta.bg}`,
        display: "flex", flexDirection: "column", gap: "0.55rem",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
        <Icon size={12} style={{ color: meta.color, filter: `drop-shadow(0 0 4px ${meta.color})` }} />
        <span style={{
          fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
          fontSize: "0.72rem", letterSpacing: "0.2em", color: meta.color,
          textShadow: `0 0 8px ${meta.glow}`,
        }}>{meta.label}</span>
      </div>
      {hit != null ? (
        <div style={{ display: "flex", alignItems: "baseline", gap: "0.35rem" }}>
          <span style={{
            fontFamily: "'JetBrains Mono',monospace", fontWeight: 800,
            fontSize: "1.4rem", color: hitColor,
            textShadow: `0 0 10px ${hitColor}80`,
            lineHeight: 1,
          }}>{hit}%</span>
          <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem", color: S.sub }}>
            hit · {stats.wins}/{sample}
          </span>
        </div>
      ) : (
        <div style={{
          fontFamily: "'JetBrains Mono',monospace", fontSize: "0.68rem",
          color: S.sub, fontStyle: "italic",
        }}>in attesa di dati</div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: "0.35rem", minHeight: 14 }}>
        {last.slice(0, 10).map((ok, i) => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: "50%",
            background: ok ? "#22FFA3" : "#FF4040",
            boxShadow: ok ? "0 0 4px #22FFA3" : "0 0 4px #FF4040",
          }} />
        ))}
        {streakHot && (
          <span style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "0.2rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
            color: "#FF6B35", textShadow: "0 0 6px rgba(255,107,53,0.6)",
          }}><Flame size={9} /> {streak}</span>
        )}
        {streakIce && (
          <span style={{
            marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: "0.2rem",
            fontFamily: "'JetBrains Mono',monospace", fontSize: "0.6rem",
            color: "#60B5FF", textShadow: "0 0 6px rgba(96,181,255,0.6)",
          }}><Snowflake size={9} /> {Math.abs(streak)}</span>
        )}
      </div>
    </div>
  );
}

function LeaderboardBanner({ leaderboard }) {
  if (!leaderboard) return null;
  return (
    <div
      data-testid="samurai-leaderboard"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
        gap: "0.6rem",
        marginBottom: "1.5rem",
      }}
    >
      {["shinobi", "samurai", "gundam", "shogun"].map((k) => (
        <SlotLeaderboardCard key={k} slotKey={k} stats={leaderboard[k]} />
      ))}
    </div>
  );
}

export default function SamuraiPage() {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [country, setCountry] = useState("all");
  const [leagueId, setLeagueId] = useState("all");
  const [teamSearch, setTeamSearch] = useState("");
  const [sortDir, setSortDir] = useState("asc"); // "asc" | "desc" (kick-off time)
  const [tierFilter, setTierFilter] = useState("none"); // "none" | "S" | "A"
  const [gradesMap, setGradesMap] = useState({}); // { [fixture_id]: "S|A|B|C" }
  const [gradesLoading, setGradesLoading] = useState(false);
  const [leaderboard, setLeaderboard] = useState(null);
  const { isPremium } = usePremium();
  const { usage, increment, refresh: refreshUsage } = useDailyUsage();
  const [quotaError, setQuotaError] = useState(null);

  // Fetch the slot leaderboard (cached 1h server-side). Ignore failures.
  useEffect(() => {
    axios.get(`${API}/samurai/leaderboard`)
      .then((r) => setLeaderboard(r.data?.slots || null))
      .catch(() => setLeaderboard(null));
  }, []);

  // Restore filters + date from the last visit (e.g. coming back from a match)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("bc_samurai_state");
      if (saved) {
        const s = JSON.parse(saved);
        if (s.date) setDate(s.date);
        if (s.country) setCountry(s.country);
        if (s.leagueId) setLeagueId(s.leagueId);
        if (s.teamSearch) setTeamSearch(s.teamSearch);
        if (s.sortDir === "asc" || s.sortDir === "desc") setSortDir(s.sortDir);
        if (s.tierFilter === "S" || s.tierFilter === "A" || s.tierFilter === "none") setTierFilter(s.tierFilter);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/fixtures/today?date=${date}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [date]);

  // Reset grades whenever the date changes (cache is per-date).
  useEffect(() => {
    setGradesMap({});
  }, [date]);

  // Lazy-fetch S/A grades only when the user actually toggles the tier filter.
  // Backend may return ``computing: true`` while a background job populates
  // the cache → poll every ~4s until done (max ~3 minutes).
  useEffect(() => {
    if (tierFilter === "none") return;
    let cancelled = false;
    let pollTimer = null;
    let tries = 0;
    const MAX_TRIES = 45; // ~3 min at 4s interval

    const fetchOnce = async () => {
      try {
        const r = await axios.get(`${API}/samurai/grades?date=${date}`);
        if (cancelled) return;
        const next = r.data?.grades || {};
        setGradesMap(next);
        const computing = !!r.data?.computing;
        if (computing && tries < MAX_TRIES) {
          tries += 1;
          pollTimer = setTimeout(fetchOnce, 4000);
          setGradesLoading(true);
        } else {
          setGradesLoading(false);
        }
      } catch {
        if (!cancelled) setGradesLoading(false);
      }
    };

    setGradesLoading(true);
    fetchOnce();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierFilter, date]);

  const countries = useMemo(() => {
    if (!data) return [];
    const set = new Map();
    (data.leagues || []).forEach((lg) => {
      if (lg.country) set.set(lg.country, (set.get(lg.country) || 0) + (lg.matches?.length || 0));
    });
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const filteredLeagues = useMemo(() => {
    if (!data) return [];
    return (data.leagues || []).filter(
      (lg) => country === "all" || lg.country === country,
    );
  }, [data, country]);

  const filteredMatches = useMemo(() => {
    if (!data) return [];
    const leagues = leagueId === "all" ? filteredLeagues
      : filteredLeagues.filter((lg) => String(lg.id) === String(leagueId));
    const all = leagues.flatMap((lg) =>
      (lg.matches || []).map((m) => ({ ...m, _league: lg })),
    );
    const q = teamSearch.trim().toLowerCase();
    const base = !q ? all : all.filter(
      (m) =>
        (m.home_team?.name || "").toLowerCase().includes(q)
        || (m.away_team?.name || "").toLowerCase().includes(q),
    );
    const sorted = [...base].sort((a, b) => {
      const ta = a.date ? new Date(a.date).getTime() : 0;
      const tb = b.date ? new Date(b.date).getTime() : 0;
      return sortDir === "asc" ? ta - tb : tb - ta;
    });
    if (tierFilter === "S" || tierFilter === "A") {
      return sorted.filter((m) => gradesMap[String(m.id)] === tierFilter);
    }
    return sorted;
  }, [filteredLeagues, leagueId, teamSearch, sortDir, tierFilter, gradesMap]);

  const goToMatch = async (m) => {
    // Free-tier daily cap: 5 Samurai picks/day (Europe/Rome midnight reset).
    if (!isPremium) {
      try {
        await increment("samurai");
      } catch (err) {
        if (err?.response?.status === 429) {
          setQuotaError(err.response.data?.detail || "Quota giornaliera esaurita");
          await refreshUsage();
          return;
        }
        // Any other failure → silently proceed (don't block on network glitch)
      }
    }
    try {
      sessionStorage.setItem("bc_samurai_scroll", String(window.scrollY || 0));
      sessionStorage.setItem("bc_samurai_state", JSON.stringify({
        date, country, leagueId, teamSearch, sortDir, tierFilter,
      }));
    } catch { /* ignore */ }
    navigate(`/samurai/${m.id}`, {
      state: {
        match: {
          id: m.id, date: m.date, status: m.status,
          goals: m.goals,
          home_team: m.home_team, away_team: m.away_team,
          league: m._league ? {
            id: m._league.id, name: m._league.name,
            country: m._league.country, logo: m._league.logo, flag: m._league.flag,
          } : null,
        },
      },
    });
  };

  // Restore scroll position after content loads (returning from a match page)
  useEffect(() => {
    if (loading || !data) return;
    const savedScroll = parseInt(sessionStorage.getItem("bc_samurai_scroll") || "0", 10);
    if (savedScroll <= 0) return;
    const raf = requestAnimationFrame(() => {
      window.scrollTo({ top: savedScroll, behavior: "instant" });
      sessionStorage.removeItem("bc_samurai_scroll");
    });
    return () => cancelAnimationFrame(raf);
  }, [loading, data]);

  return (
    <div data-testid="samurai-page" style={{ minHeight: "100vh", background: S.bg, color: S.ink }}>
      <SamuraiBackground />

      <Navbar />

      <div style={{ position: "relative", maxWidth: 960, margin: "0 auto", padding: "2rem 1.5rem 5rem", zIndex: 2 }}>
        {/* HERO */}
        <div style={{ textAlign: "center", marginBottom: "2rem" }}>
          <div
            style={{
              display: "inline-flex", alignItems: "center", gap: "0.75rem",
              padding: "0.4rem 1rem",
              background: S.bloodDim,
              border: `1px solid ${S.blood}60`,
              boxShadow: `0 0 18px ${S.bloodGlow}, inset 0 0 14px ${S.bloodDim}`,
              marginBottom: "1rem",
            }}
          >
            <Sword size={12} style={{ color: S.blood, filter: `drop-shadow(0 0 6px ${S.blood})` }} />
            <span
              style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.72rem", letterSpacing: "0.3em", color: S.blood,
                textShadow: `0 0 10px ${S.bloodGlow}`,
              }}
            >
              SAMURAI PICKER · 刀
            </span>
          </div>
          <h1
            style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
              fontSize: "clamp(2.2rem, 5vw, 3.6rem)", lineHeight: 1,
              letterSpacing: "-0.02em", margin: "0 0 0.6rem",
            }}
          >
            UN COLPO,
            <br />
            <span style={{ color: S.blood, textShadow: `0 0 32px ${S.bloodGlow}, 0 0 64px ${S.bloodGlow}` }}>
              QUATTRO PRONOSTICI.
            </span>
          </h1>
          <p
            style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.95rem",
              color: S.sub, maxWidth: 560, margin: "0 auto",
            }}
          >
            Scegli il giorno, seleziona la partita. Il Samurai forgia{" "}
            <strong style={{ color: S.steel, textShadow: `0 0 8px ${S.steelGlow}` }}>Shinobi</strong>,{" "}
            <strong style={{ color: S.gold, textShadow: `0 0 8px ${S.goldGlow}` }}>Samurai</strong>,{" "}
            <strong style={{ color: S.gundam, textShadow: `0 0 8px ${S.gundamGlow}` }}>Gundam</strong> e{" "}
            <strong style={{ color: S.shogun, textShadow: `0 0 8px ${S.shogunGlow}` }}>Shogun</strong>.
          </p>
        </div>

        <KatanaDivider style={{ marginBottom: "1.5rem" }} />

        {/* SLOT LEADERBOARD (last 30 days, cached 1h) */}
        <LeaderboardBanner leaderboard={leaderboard} />

        {/* Free-tier daily quota indicator + paywall */}
        {!isPremium && usage && (
          <div
            data-testid="samurai-quota-banner"
            onClick={() => { if (quotaError || usage.samurai.remaining === 0) navigate("/pricing"); }}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              gap: 12, padding: "0.7rem 1rem", marginBottom: "1rem",
              background: (quotaError || usage.samurai.remaining === 0)
                ? "rgba(239,68,68,0.08)"
                : "rgba(200,255,0,0.04)",
              border: `1px solid ${(quotaError || usage.samurai.remaining === 0) ? "rgba(239,68,68,0.45)" : "rgba(200,255,0,0.4)"}`,
              color: (quotaError || usage.samurai.remaining === 0) ? "#EF4444" : "#C8FF00",
              cursor: (quotaError || usage.samurai.remaining === 0) ? "pointer" : "default",
              fontFamily: "'JetBrains Mono',monospace",
              fontSize: "0.74rem",
              letterSpacing: "0.06em",
            }}
          >
            <span>
              {quotaError
                ? `⛔ ${quotaError}`
                : `⚡ Samurai Picker · ${usage.samurai.used}/${usage.samurai.limit} oggi · ${usage.samurai.remaining} rimasti (Free)`}
            </span>
            <span style={{
              padding: "0.25rem 0.6rem",
              border: "1px solid currentColor", borderRadius: 2,
              fontSize: "0.62rem", letterSpacing: "0.16em", textTransform: "uppercase",
              fontWeight: 700,
            }}>
              {(quotaError || usage.samurai.remaining === 0) ? "Sblocca Premium →" : "Premium illimitato →"}
            </span>
          </div>
        )}

        {/* Ronin parlay entry point */}
        <a
          data-testid="samurai-ronin-cta"
          href="/samurai/ronin"
          style={{
            display: "flex", alignItems: "center", gap: "0.9rem",
            padding: "1rem 1.1rem",
            marginBottom: "1.5rem",
            background: "linear-gradient(135deg, rgba(10,12,14,0.95) 0%, rgba(246,198,67,0.08) 50%, rgba(10,12,14,0.95) 100%)",
            border: "1px solid rgba(246,198,67,0.4)",
            boxShadow: "0 0 18px -6px rgba(246,198,67,0.35), inset 0 0 14px rgba(0,0,0,0.4)",
            color: S.ink,
            textDecoration: "none",
            transition: "all 220ms ease",
            cursor: "pointer",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#F6C643"; e.currentTarget.style.boxShadow = "0 0 26px -4px rgba(246,198,67,0.55), inset 0 0 14px rgba(0,0,0,0.4)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(246,198,67,0.4)"; e.currentTarget.style.boxShadow = "0 0 18px -6px rgba(246,198,67,0.35), inset 0 0 14px rgba(0,0,0,0.4)"; }}
        >
          <Sword size={22} style={{ color: "#F6C643", filter: "drop-shadow(0 0 6px rgba(246,198,67,0.7))" }} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 900,
              fontSize: "0.95rem", letterSpacing: "0.24em", color: "#F6C643",
              textShadow: "0 0 8px rgba(246,198,67,0.5)",
              textTransform: "uppercase",
            }}>La Multipla del Ronin</div>
            <div style={{
              fontFamily: "'DM Sans',sans-serif", fontSize: "0.72rem", color: S.sub,
              fontStyle: "italic", marginTop: 2,
            }}>Tre samurai senza padrone. Scegli il tuo coach, imposta la quota, lancia la schedina.</div>
          </div>
          <ChevronRight size={16} style={{ color: "#F6C643" }} />
        </a>

        {/* DATE PICKER */}
        <DatePicker value={date} onChange={setDate} />

        {/* FILTER BAR */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 2fr",
            gap: "0.5rem",
            marginBottom: "1.5rem",
          }}
          data-testid="samurai-filter-bar"
        >
          <select
            data-testid="samurai-country-select"
            value={country}
            onChange={(e) => { setCountry(e.target.value); setLeagueId("all"); }}
            style={{
              background: S.panel, border: `1px solid ${S.hairline2}`, color: S.ink,
              padding: "0.8rem 0.9rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
              letterSpacing: "0.1em", textTransform: "uppercase",
              outline: "none", cursor: "pointer",
            }}
          >
            <option value="all">◆ Tutti i paesi ({data?.total || 0})</option>
            {countries.map(([c, n]) => (
              <option key={c} value={c}>{c} ({n})</option>
            ))}
          </select>
          <select
            data-testid="samurai-league-select"
            value={leagueId}
            onChange={(e) => setLeagueId(e.target.value)}
            style={{
              background: S.panel, border: `1px solid ${S.hairline2}`, color: S.ink,
              padding: "0.8rem 0.9rem",
              fontFamily: "'JetBrains Mono',monospace", fontSize: "0.78rem",
              letterSpacing: "0.1em", textTransform: "uppercase",
              outline: "none", cursor: "pointer",
            }}
          >
            <option value="all">◆ Tutte le leghe</option>
            {filteredLeagues.map((lg) => (
              <option key={lg.id} value={lg.id}>
                {lg.name} ({lg.matches?.length || 0})
              </option>
            ))}
          </select>
          <div style={{ position: "relative" }}>
            <Search
              size={14}
              style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                color: S.muted,
              }}
            />
            <input
              data-testid="samurai-team-search"
              value={teamSearch}
              onChange={(e) => setTeamSearch(e.target.value)}
              placeholder="Cerca squadra..."
              style={{
                width: "100%", background: S.panel, border: `1px solid ${S.hairline2}`,
                color: S.ink, padding: "0.8rem 0.9rem 0.8rem 2.2rem",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", outline: "none",
              }}
            />
          </div>
        </div>

        {/* MATCH LIST */}
        <div
          style={{
            background: S.panel, border: `1px solid ${S.hairline2}`,
            borderLeft: `2px solid ${S.blood}`,
            boxShadow: `-8px 0 22px -10px ${S.bloodGlow}`,
          }}
        >
          <div
            style={{
              padding: "0.9rem 1.1rem", borderBottom: `1px solid ${S.hairline}`,
              display: "flex", alignItems: "center", gap: "0.5rem",
            }}
          >
            <Target size={13} style={{ color: S.blood, filter: `drop-shadow(0 0 4px ${S.blood})` }} />
            <span
              style={{
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.72rem", letterSpacing: "0.25em", color: S.muted,
                textTransform: "uppercase",
              }}
            >
              Partite · {formatDateLabel(date)}
            </span>
            <span
              style={{
                marginLeft: "auto",
                fontFamily: "'JetBrains Mono',monospace",
                fontSize: "0.72rem", color: S.sub,
              }}
            >
              {filteredMatches.length} risultati
            </span>
            <TierToggle
              tier="S"
              active={tierFilter === "S"}
              loading={gradesLoading && tierFilter === "S"}
              onClick={() => setTierFilter((t) => (t === "S" ? "none" : "S"))}
            />
            <TierToggle
              tier="A"
              active={tierFilter === "A"}
              loading={gradesLoading && tierFilter === "A"}
              onClick={() => setTierFilter((t) => (t === "A" ? "none" : "A"))}
            />
            <button
              data-testid="samurai-sort-toggle"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              style={{
                display: "inline-flex", alignItems: "center", gap: "0.4rem",
                padding: "0.35rem 0.65rem",
                background: S.bloodDim,
                border: `1px solid ${S.blood}60`,
                color: S.blood, cursor: "pointer",
                fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 700,
                fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase",
                textShadow: `0 0 8px ${S.bloodGlow}`,
                boxShadow: `0 0 10px ${S.bloodGlow}, inset 0 0 6px ${S.bloodDim}`,
              }}
              title={sortDir === "asc" ? "Più presto prima" : "Più tardi prima"}
            >
              <ArrowDownUp
                size={11}
                style={{
                  transform: sortDir === "desc" ? "scaleY(-1)" : "none",
                  transition: "transform 0.2s ease",
                }}
              />
              {sortDir === "asc" ? "Orario ↑" : "Orario ↓"}
            </button>
          </div>

          {loading ? (
            <div style={{ padding: "3rem 1rem", textAlign: "center" }}>
              <Loader2
                size={18}
                style={{ color: S.blood, animation: "spin 0.8s linear infinite", margin: "0 auto" }}
              />
            </div>
          ) : filteredMatches.length === 0 ? (
            <div
              style={{
                padding: "3rem 1rem", textAlign: "center",
                fontFamily: "'DM Sans',sans-serif", fontSize: "0.9rem", color: S.muted,
              }}
            >
              Nessuna partita corrisponde ai filtri per questo giorno.
            </div>
          ) : (
            filteredMatches.map((m, idx) => {
              const home = m.home_team || {};
              const away = m.away_team || {};
              const time = formatKickoff(m.date);
              const lg = m._league || {};
              const finished = ["FT", "AET", "PEN"].includes(m.status);
              const final = finished && m.goals
                ? `${m.goals.home ?? "?"}–${m.goals.away ?? "?"}`
                : null;
              return (
                <button
                  key={m.id}
                  data-testid={`samurai-match-${m.id}`}
                  onClick={() => goToMatch(m)}
                  style={{
                    width: "100%", textAlign: "left",
                    background: "transparent", border: "none",
                    borderBottom: idx === filteredMatches.length - 1 ? "none" : `1px solid ${S.hairline}`,
                    padding: "1rem 1.1rem", color: S.ink, cursor: "pointer",
                    display: "grid",
                    gridTemplateColumns: "66px 1fr auto",
                    alignItems: "center", gap: "1rem",
                    transition: "background 0.15s ease, padding-left 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = S.bloodDim;
                    e.currentTarget.style.paddingLeft = "1.4rem";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.paddingLeft = "1.1rem";
                  }}
                >
                  {/* Time / Final score */}
                  <div style={{ textAlign: "center" }}>
                    {final ? (
                      <>
                        <div
                          style={{
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "1.05rem", fontWeight: 700, color: S.blood,
                            textShadow: `0 0 10px ${S.bloodGlow}`,
                            letterSpacing: "0.04em", lineHeight: 1,
                          }}
                        >
                          {final}
                        </div>
                        <div
                          style={{
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.55rem", color: S.muted, letterSpacing: "0.15em",
                            textTransform: "uppercase", marginTop: 4,
                          }}
                        >
                          {m.status}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={{
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.95rem", fontWeight: 700, color: S.ink,
                            letterSpacing: "0.04em", lineHeight: 1,
                          }}
                        >
                          {time}
                        </div>
                        <div
                          style={{
                            fontFamily: "'JetBrains Mono',monospace",
                            fontSize: "0.55rem", color: S.muted, letterSpacing: "0.15em",
                            textTransform: "uppercase", marginTop: 4,
                          }}
                        >
                          KICK-OFF
                        </div>
                      </>
                    )}
                  </div>

                  {/* Teams */}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.3rem" }}>
                      {home.logo && (
                        <img src={home.logo} alt=""
                          style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <span
                        style={{
                          fontFamily: "'DM Sans',sans-serif", fontSize: "1rem",
                          fontWeight: 600, color: S.ink, lineHeight: 1.15,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {home.name}
                      </span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.55rem", marginBottom: "0.4rem" }}>
                      {away.logo && (
                        <img src={away.logo} alt=""
                          style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0 }}
                          onError={(e) => { e.target.style.display = "none"; }}
                        />
                      )}
                      <span
                        style={{
                          fontFamily: "'DM Sans',sans-serif", fontSize: "1rem",
                          fontWeight: 600, color: S.ink, lineHeight: 1.15,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}
                      >
                        {away.name}
                      </span>
                    </div>
                    <div
                      style={{
                        fontFamily: "'JetBrains Mono',monospace",
                        fontSize: "0.62rem", color: S.muted, letterSpacing: "0.12em",
                        textTransform: "uppercase",
                      }}
                    >
                      {lg.country} · {lg.name}
                    </div>
                  </div>

                  <ChevronRight size={18}
                    style={{ color: S.blood, flexShrink: 0, filter: `drop-shadow(0 0 6px ${S.bloodGlow})` }}
                  />
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}


const TIER_META = {
  S: { color: "#22FFA3", glow: "rgba(34,255,163,0.55)" }, // neon green
  A: { color: "#9DF6FF", glow: "rgba(157,246,255,0.50)" }, // neon cyan
};

function TierToggle({ tier, active, loading, onClick }) {
  const meta = TIER_META[tier];
  return (
    <button
      onClick={onClick}
      data-testid={`samurai-tier-toggle-${tier.toLowerCase()}`}
      title={active ? `Mostra tutte` : `Solo ${tier} tier del giorno`}
      style={{
        display: "inline-flex", alignItems: "center", gap: "0.4rem",
        padding: "0.35rem 0.65rem",
        background: active
          ? `color-mix(in srgb, ${meta.color} 18%, transparent)`
          : "transparent",
        border: `1px solid ${active ? meta.color : `${meta.color}40`}`,
        color: active ? meta.color : `${meta.color}B0`,
        cursor: "pointer",
        fontFamily: "'Barlow Condensed',sans-serif", fontWeight: 800,
        fontSize: "0.65rem", letterSpacing: "0.22em", textTransform: "uppercase",
        textShadow: active ? `0 0 8px ${meta.glow}` : "none",
        boxShadow: active ? `0 0 10px ${meta.glow}, inset 0 0 6px color-mix(in srgb, ${meta.color} 14%, transparent)` : "none",
        transition: "all 0.18s ease",
        opacity: loading ? 0.65 : 1,
      }}
    >
      {loading ? (
        <Loader2 size={11} style={{ animation: "spin 0.8s linear infinite" }} />
      ) : (
        <Shield size={11} />
      )}
      {tier} TIER
    </button>
  );
}
